import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Building2,
  ClipboardCheck,
  Droplets,
  Factory,
  FlaskConical,
  Layers,
  Paintbrush,
  Phone,
  ShieldCheck,
  ShieldHalf,
  Users,
  UtensilsCrossed,
  Warehouse,
  Wrench,
  Download,
} from 'lucide-react';
import { ABOUT_HERO_IMAGE, ABOUT_SIDE_IMAGE } from '../data/siteGallery';
import { BROCHURE_PDF } from '../data/siteContent';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page bg-[#f4f6f9]">
      {/* Hero — imagery + overlay (industrial flooring context, not generic automation) */}
      <section className="relative overflow-hidden pb-20 pt-12 md:pb-24 md:pt-16">
        <div className="absolute inset-0">
          <img src={ABOUT_HERO_IMAGE} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-950/70 via-brand-900/55 to-[#06202d]/60" />
          <div className="absolute inset-0 industrial-grid opacity-[0.08]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-brand-200/90">
            <Link to="/" className="transition-colors hover:text-white">
              Home
            </Link>
            <span className="text-brand-400/80">/</span>
            <span className="text-white">About us</span>
          </nav>
          <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-200">
            Sai Vikas Group
          </div>
          <h1 className="font-heading max-w-4xl text-4xl font-extrabold tracking-tight text-white md:text-6xl md:leading-[1.08]">
            About <span className="text-brand-300">Sai Vikas Coatings</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-100/95 md:text-lg">
            Clean room and industrial flooring from Pune — serving pharmaceuticals, food, warehouses, and engineering sectors across India and abroad.
          </p>
        </div>
      </section>

      {/* Split + letter copy */}
      <section className="relative z-10 mx-auto -mt-10 max-w-6xl px-4 pb-6 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_12px_48px_rgba(13,40,71,0.08)]">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-stretch">
            <div className="relative min-h-[280px] lg:min-h-[420px]">
              <img src={ABOUT_SIDE_IMAGE} alt="Industrial flooring and coating work" className="h-full w-full object-cover" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-brand-950/55 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-md">
                <p className="font-heading text-sm font-bold uppercase tracking-wider text-white">Pune · India &amp; abroad</p>
                <p className="mt-1 text-sm text-white/90">Authorised distributor &amp; applicator — trained teams, project execution nationwide.</p>
              </div>
            </div>
            <div className="flex flex-col justify-center p-8 md:p-10 lg:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Introduction</p>
              <h2 className="font-heading mt-2 text-2xl font-bold text-neutral-900 md:text-3xl">Who we are</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-neutral-700 md:text-base">
                We are pleased to introduce ourselves,{' '}
                <strong className="font-semibold text-neutral-900">&quot;Sai Vikas Coatings Pvt. Ltd.&quot;</strong>, a Sai Vikas Group company based in Pune, engaged in providing clean room solutions mainly for{' '}
                <strong className="text-neutral-900">pharmaceuticals, food, warehouses, and engineering industries</strong>.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-neutral-700 md:text-base">
                <strong className="text-neutral-900">Sai Vikas Coatings Pvt. Ltd.</strong> offers mainly the following services along with supply of material:
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services — brochure list 1–4 as cards */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="font-heading text-center text-2xl font-bold text-neutral-900 md:text-3xl">What we offer</h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-neutral-600">
          Flooring, wall coatings, coving, and waterproofing — supplied and applied with documented systems.
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {[
            {
              icon: Layers,
              title: 'Industrial flooring',
              desc: 'Epoxy and polyurethane based self levelling flooring',
            },
            {
              icon: Paintbrush,
              title: 'Protective / interior wall coating',
              desc: 'Solvent and water based epoxy and polyurethane coating',
            },
            {
              icon: ShieldHalf,
              title: 'Coving',
              desc: 'High strength epoxy based coving with polyurethane finish',
            },
            {
              icon: Droplets,
              title: 'Waterproofing system',
              desc: 'Acrylic based waterproofing systems for arresting leakages and seepages',
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group relative overflow-hidden rounded-2xl border border-neutral-200/90 bg-white p-6 shadow-sm transition hover:border-brand-200/80 hover:shadow-[0_12px_40px_rgba(13,40,71,0.08)]"
            >
              <div className="absolute right-0 top-0 h-24 w-24 translate-x-6 -translate-y-6 rounded-full bg-brand-50/80 opacity-0 transition group-hover:opacity-100" />
              <div className="relative flex gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-brand-100/80 text-brand-800 ring-1 ring-brand-200/50">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-neutral-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Narrative — styled document, not a single grey block */}
      <section className="mx-auto max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_minmax(0,340px)] lg:gap-12">
          <div className="space-y-6 rounded-2xl border border-neutral-200/80 bg-white p-8 shadow-[0_4px_24px_rgba(13,40,71,0.05)] md:p-10">
            <div className="border-l-4 border-brand-500 pl-5">
              <p className="text-[15px] leading-relaxed text-neutral-700 md:text-base">
                We are also supplying epoxy material which is imported in bulk, repacked and marketed by Sai Vikas Coatings Pvt. Ltd. We are{' '}
                <strong className="text-neutral-900">authorised distributor and applicators</strong> of{' '}
                <strong className="text-neutral-900">M/s. ELANTAS Beck India Ltd.</strong> (formerly known as Dr. Beck and Co. India Ltd.) for their range of industrial floorings, protective coatings and waterproofing compounds. ELANTAS Beck India Ltd. is a German based company.
              </p>
            </div>
            <p className="text-[15px] leading-relaxed text-neutral-700 md:text-base">
              Also we are authorised distributors of{' '}
              <strong className="text-neutral-900">Tremco CPG (India) Private Limited</strong> (formerly known as Flowcrete India Private Limited), USA based company,{' '}
              <strong className="text-neutral-900">Sika India Pvt. Ltd.</strong> and <strong className="text-neutral-900">Asian Paints</strong>. We have a well trained team of{' '}
              <strong className="text-neutral-900">90 technicians</strong> to take care of various projects all over India and abroad. The dedicated team of our technicians is trained at the technical service department of ELANTAS Beck. They are regularly trained in order to upgrade their skills in the latest application techniques.
            </p>
            <p className="text-[15px] leading-relaxed text-neutral-700 md:text-base">
              We have carried out many major projects for pharmaceuticals, food, automobiles, and engineering industries. Please refer to our{' '}
              <Link to="/clients" className="font-semibold text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-600">
                satisfied clients
              </Link>{' '}
              for your reference. We have good hand in this field from the <strong className="text-neutral-900">last three decades</strong>.
            </p>
          </div>

          <aside className="space-y-5 lg:pt-2">
            <div className="rounded-2xl border border-brand-200/60 bg-gradient-to-br from-brand-50/90 to-white p-6 shadow-sm">
              <h3 className="font-heading text-sm font-bold uppercase tracking-[0.15em] text-brand-800">Partners &amp; authorisations</h3>
              <ul className="mt-4 space-y-3 text-sm text-neutral-700">
                <li className="flex gap-2">
                  <Factory className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">ELANTAS Beck India Ltd.</strong> — industrial flooring, coatings, waterproofing
                  </span>
                </li>
                <li className="flex gap-2">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">Tremco CPG (India)</strong> — Flowcrete range
                  </span>
                </li>
                <li className="flex gap-2">
                  <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">Sika India Pvt. Ltd.</strong>
                  </span>
                </li>
                <li className="flex gap-2">
                  <Paintbrush className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">Asian Paints</strong>
                  </span>
                </li>
                <li className="flex gap-2">
                  <Factory className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">BASF India Ltd.</strong>
                  </span>
                </li>
                <li className="flex gap-2">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">Pidilite Industries</strong>
                  </span>
                </li>
                <li className="flex gap-2">
                  <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
                  <span>
                    <strong className="text-neutral-900">Fosroc India Pvt Ltd.</strong>
                  </span>
                </li>
              </ul>
            </div>
            <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/80 p-6 text-sm leading-relaxed text-neutral-600">
              Quality references: ISO 9001:2015, CFTRI, CIPET, HACCP-related compliance as applicable — details on request.
            </div>
          </aside>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-neutral-200/80 bg-white py-14">
        <div className="mx-auto grid max-w-5xl gap-6 px-4 sm:grid-cols-3 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-neutral-100 bg-gradient-to-b from-white to-neutral-50/80 p-8 text-center shadow-sm">
            <Users className="mx-auto mb-3 h-9 w-9 text-brand-700" strokeWidth={1.75} />
            <div className="font-heading text-4xl font-bold text-neutral-900">90</div>
            <div className="mt-1 text-sm font-medium text-neutral-600">Trained technicians</div>
          </div>
          <div className="rounded-2xl border border-neutral-100 bg-gradient-to-b from-white to-neutral-50/80 p-8 text-center shadow-sm">
            <Award className="mx-auto mb-3 h-9 w-9 text-brand-700" strokeWidth={1.75} />
            <div className="font-heading text-4xl font-bold text-neutral-900">30+</div>
            <div className="mt-1 text-sm font-medium text-neutral-600">Years in the field</div>
          </div>
          <div className="rounded-2xl border border-neutral-100 bg-gradient-to-b from-white to-neutral-50/80 p-8 text-center shadow-sm">
            <Building2 className="mx-auto mb-3 h-9 w-9 text-brand-700" strokeWidth={1.75} />
            <div className="font-heading text-xl font-bold leading-snug text-neutral-900">India &amp; abroad</div>
            <div className="mt-1 text-sm font-medium text-neutral-600">Project execution</div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-center text-2xl font-bold text-neutral-900 md:text-3xl">Sectors we serve</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-neutral-600">
            Clean room and industrial solutions aligned with your industry requirements.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: FlaskConical, title: 'Pharmaceuticals', text: 'Clean room and hygienic environments.' },
              { icon: UtensilsCrossed, title: 'Food & beverage', text: 'Processing and compliant floor and wall systems.' },
              { icon: Warehouse, title: 'Warehouses', text: 'Durable flooring for storage and logistics.' },
              { icon: ShieldCheck, title: 'Engineering', text: 'Automotive and industrial plant flooring.' },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-neutral-200/90 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-brand-100/50 transition group-hover:scale-110" />
                <div className="relative mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-800 ring-1 ring-brand-200/40">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-lg font-bold text-neutral-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality + CTA */}
      <section className="border-t border-neutral-200 bg-[#f0f4f8] py-14">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-neutral-900">Quality &amp; compliance</h2>
          <p className="mt-4 leading-relaxed text-neutral-600">
            Our operations are supported by relevant certifications and testing partnerships (e.g. ISO 9001:2015, CFTRI, CIPET, HACCP-related compliance) as applicable to our product and service scope. Details are available on request.
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-br from-brand-800 to-brand-950 py-16">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">Discuss your project</h2>
          <p className="mt-3 text-brand-100/90">Share your site requirements — flooring, walls, coving, or waterproofing.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-small bg-white px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wide text-brand-900 shadow-lg transition hover:bg-brand-50"
            >
              <ClipboardCheck className="h-5 w-5" />
              Request a quote
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-small border border-white/30 bg-transparent px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wide text-white transition hover:bg-white/10"
            >
              <Phone className="h-5 w-5" />
              Contact us
            </Link>
            <a
              href={BROCHURE_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-small border border-white/30 bg-transparent px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wide text-white transition hover:bg-white/10"
            >
              <Download className="h-5 w-5" />
              Download brochure
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
